import type { FormGroup } from '@angular/forms';

export interface ProtocoloResumoConsulta {
    portal?: string;
    sas?: string;
    numeroProtocolo?: string;
    anoProtocolo?: string;
    ufCrm?: string;
    inPago?: boolean;
    isAnuidadePaga?: boolean;
}

export interface RequisicaoProtocolo {
    numeroProtocolo: string;
    anoProtocolo: string;
    ufCrm: string;
}

export type MapaAliasesFormulario = Record<string, string | string[]>;
export type MapaTransformadoresFormulario = Record<string, (value: unknown) => unknown>;

export function montarRequestProtocolo(protocoloSelecionado: ProtocoloResumoConsulta | null | undefined, codigoEstado: string): RequisicaoProtocolo | null {
    const numeroDireto = valorTexto(protocoloSelecionado?.numeroProtocolo).trim();
    const anoDireto = valorTexto(protocoloSelecionado?.anoProtocolo).trim();
    const protocoloDireto = numeroDireto && anoDireto ? ([numeroDireto, anoDireto] as [string, string]) : null;

    const portal = valorTexto(protocoloSelecionado?.portal).trim();
    const sas = valorTexto(protocoloSelecionado?.sas).trim();
    const protocolo = protocoloDireto ?? extrairNumeroAno(portal) ?? extrairNumeroAno(sas);

    const ufCrmDireto = valorTexto(protocoloSelecionado?.ufCrm).trim().toUpperCase();
    const ufCrm = ufCrmDireto || extrairUfCrm(codigoEstado);

    if (!protocolo || !ufCrm) {
        return null;
    }

    const [numeroProtocolo, anoProtocolo] = protocolo;
    return { numeroProtocolo, anoProtocolo, ufCrm };
}

export function montarPatchFormulario(form: FormGroup, response: Record<string, unknown>, aliases: MapaAliasesFormulario = {}, transformadores: MapaTransformadoresFormulario = {}): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    const indiceNormalizado = new Map<string, unknown>();

    for (const [chave, valor] of Object.entries(response)) {
        indiceNormalizado.set(normalizarChave(chave), valor);
    }

    for (const nomeControle of Object.keys(form.controls)) {
        const aliasesControle = aliases[nomeControle];
        const chavesBusca = Array.isArray(aliasesControle) ? aliasesControle : aliasesControle ? [aliasesControle] : [];

        let valor: unknown;
        for (const chaveBusca of chavesBusca) {
            valor = obterValorResposta(response, chaveBusca, indiceNormalizado);
            if (valor !== undefined) {
                break;
            }
        }

        if (valor === undefined) {
            valor = indiceNormalizado.get(normalizarChave(nomeControle));
        }

        if (valor === undefined) {
            continue;
        }

        const transformador = transformadores[nomeControle];
        patch[nomeControle] = transformador ? transformador(valor) : valor;
    }

    return patch;
}

export function valorTexto(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
}

export function formatarDataMascara(valor: unknown): string {
    const texto = valorTexto(valor).trim();
    if (!texto) {
        return '';
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
        return texto;
    }

    const data = texto.split('T')[0];
    const partes = data.split('-');
    if (partes.length === 3) {
        const [ano, mes, dia] = partes;
        if (ano && mes && dia) {
            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
        }
    }

    return texto;
}

export function formatarDataHoraMascara(valor: unknown): string {
    const texto = valorTexto(valor).trim();
    if (!texto) {
        return '';
    }

    if (/^\d{2}\/\d{2}\/\d{4}\s\d{2}:\d{2}$/.test(texto)) {
        return texto;
    }

    const [dataParte, horaParte] = texto.split('T');
    const dataFormatada = formatarDataMascara(dataParte);
    const hora = (horaParte ?? '').slice(0, 5);

    if (dataFormatada && hora) {
        return `${dataFormatada} ${hora}`;
    }

    return dataFormatada || texto;
}

export function parseDate(valor: unknown): Date | null {
    const texto = valorTexto(valor).trim();
    if (!texto) {
        return null;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
        const [dia, mes, ano] = texto.split('/');
        return new Date(Number(ano), Number(mes) - 1, Number(dia));
    }

    const data = new Date(texto);
    if (Number.isNaN(data.getTime())) {
        return null;
    }

    return data;
}

export function simNaoBoolean(valor: unknown): boolean | null {
    const texto = valorTexto(valor).trim().toUpperCase();
    if (!texto) {
        return null;
    }

    if (['S', 'SIM', 'TRUE', '1'].includes(texto)) {
        return true;
    }

    if (['N', 'NAO', 'NÃO', 'FALSE', '0'].includes(texto)) {
        return false;
    }

    return null;
}

/**
 * Id de domínio (país, município) vindo da API como texto, convertido para o `number` que as listas
 * de `p-select` usam em `value`. Sem isto o casamento é `'5300108' === 5300108`, que é falso, e o
 * select renderiza o placeholder mesmo com o dado presente na resposta.
 */
export function paraNumero(valor: unknown): number | null {
    if (typeof valor === 'number') {
        return Number.isFinite(valor) ? valor : null;
    }

    const texto = valorTexto(valor).trim();
    if (!texto) {
        return null;
    }

    const numero = Number(texto);
    return Number.isFinite(numero) ? numero : null;
}

function extrairNumeroAno(valor: string): [string, string] | null {
    if (!valor) {
        return null;
    }

    const match = valor.match(/(\d+)\s*\/\s*(\d{4})/);
    if (!match) {
        return null;
    }

    const numeroProtocolo = valorTexto(match[1]).trim();
    const anoProtocolo = valorTexto(match[2]).trim();
    if (!numeroProtocolo || !anoProtocolo) {
        return null;
    }

    return [numeroProtocolo, anoProtocolo];
}

function extrairUfCrm(valor: string): string {
    const texto = valorTexto(valor).trim().toUpperCase();
    if (!texto) {
        return '';
    }

    const match = texto.match(/([A-Z]{2})$/);
    return match?.[1] ?? '';
}

function obterValorResposta(response: Record<string, unknown>, chaveBusca: string, indiceNormalizado: Map<string, unknown>): unknown {
    if (!chaveBusca) {
        return undefined;
    }

    if (chaveBusca.includes('.')) {
        return obterValorPorCaminho(response, chaveBusca);
    }

    if (Object.prototype.hasOwnProperty.call(response, chaveBusca)) {
        return response[chaveBusca];
    }

    return indiceNormalizado.get(normalizarChave(chaveBusca));
}

function obterValorPorCaminho(source: Record<string, unknown>, path: string): unknown {
    const partes = path.split('.');
    let atual: unknown = source;

    for (const parte of partes) {
        if (!atual || typeof atual !== 'object' || !(parte in (atual as Record<string, unknown>))) {
            return undefined;
        }

        atual = (atual as Record<string, unknown>)[parte];
    }

    return atual;
}

function normalizarChave(chave: string): string {
    return valorTexto(chave)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toLowerCase();
}
